// Composant d'entrée décimal pour les formulaires U2000 (sans symbole monétaire)
// Inspiré de U2000InputCurrency : même formatage (séparateurs, décimales) sans afficher le symbole devise
import React, { useRef } from "react";
import U2000Label from "../label/U2000Label";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import {
  formatCurrency,
  parseCurrencyInput,
  getNewCursorPosition,
  isPartialDecimalInput,
  type CurrencyFormatOptions,
} from "../../../tools/U2000CurrencyUtils";
import { u2000SelectInputContentsOnFocus } from "../../../tools/U2000FormInputUtils";

interface U2000InputDecimalProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label: string;
  readonly?: boolean;
  required?: boolean;
  placeholder?: string;
  icon?: React.ReactNode;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  decimalSeparator?: "." | ",";
  thousandSeparator?: " " | ",";
  allowNegative?: boolean;
  showNegativeInRed?: boolean;
  showNegativeInParentheses?: boolean;
  decimalDigits?: number;
  ignoreFormReadState?: boolean;
  /** Alignement du texte saisi (ex. montants à droite comme U2000InputCurrency). */
  textAlign?: "left" | "right";
}

const getInputClasses = (
  formStateHandlers: U2000FormStateHandlers<any>,
  fieldName: string,
  isFocused: boolean,
  readonly?: boolean,
  showNegativeInRed?: boolean,
  isNegative?: boolean,
  ignoreFormReadState?: boolean,
  textAlign: "left" | "right" = "left",
) => {
  const alignClass = textAlign === "right" ? "text-right" : "text-left";
  const baseClasses = `w-full px-4 py-3 text-sm ${alignClass} border rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2`;
  const isDisabled = readonly || (!ignoreFormReadState && formStateHandlers.getFormState() === U2000FormStateEnumState.READ);
  const err = formStateHandlers.getErrors()[fieldName];
  const hasError =
    (formStateHandlers.getTouched()[fieldName] && err !== undefined) ||
    (formStateHandlers.isSubmitted() && err !== undefined);

  if (isDisabled) {
    return `${baseClasses} bg-muted text-gray-500 border-gray-200 cursor-not-allowed`;
  }

  if (hasError) {
    return `${baseClasses} bg-surface border-red-300 focus:border-red-500 focus:ring-red-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  if (isFocused) {
    const negativeClass = showNegativeInRed && isNegative ? "text-red-600" : "text-gray-900";
    return `${baseClasses} bg-surface border-blue-500 ring-blue-500/20 ${negativeClass} placeholder:text-gray-400`;
  }

  const negativeClass = showNegativeInRed && isNegative ? "text-red-600" : "text-gray-900";
  return `${baseClasses} bg-surface border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 ${negativeClass} placeholder:text-gray-400 hover:border-gray-400`;
};

function getNestedValue(formStateHandlers: U2000FormStateHandlers<any>, fieldName: string): any {
  const data = formStateHandlers.getData() as Record<string, any>;
  return fieldName.split(".").reduce((acc: any, part: string) => (acc !== undefined && acc !== null ? acc[part] : undefined), data);
}

const U2000InputDecimal = ({
  formStateHandlers,
  fieldName,
  label,
  readonly = false,
  required = false,
  placeholder,
  icon,
  onFocus,
  onBlur,
  inputRef,
  decimalSeparator = ".",
  thousandSeparator = " ",
  allowNegative = true,
  showNegativeInRed = false,
  showNegativeInParentheses = false,
  decimalDigits = 2,
  ignoreFormReadState = false,
  textAlign = "left",
}: U2000InputDecimalProps) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [displayValue, setDisplayValue] = React.useState<string>("");
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputElementRef = inputRef || internalInputRef;
  const previousValueRef = useRef<string>("");
  /** true si le focus a vidé une cellule à 0 — au blur vide, restaurer 0. */
  const restoreZeroOnEmptyBlurRef = useRef(false);

  const formatOptions: CurrencyFormatOptions = {
    currencySymbol: "",
    symbolPosition: "left",
    decimalSeparator,
    thousandSeparator,
    allowNegative,
    showNegativeInParentheses,
    decimalDigits,
  };

  const currentNumericValue = getNestedValue(formStateHandlers, fieldName);
  const isDisabled = readonly || (!ignoreFormReadState && formStateHandlers.getFormState() === U2000FormStateEnumState.READ);

  React.useEffect(() => {
    if (isDisabled && isFocused) {
      setIsFocused(false);
    }

    if ((!isFocused || isDisabled) && currentNumericValue !== null && currentNumericValue !== undefined && currentNumericValue !== "") {
      const formatted = formatCurrency(currentNumericValue, formatOptions);
      if (formatted !== displayValue) {
        setDisplayValue(formatted);
      }
    } else if ((!isFocused || isDisabled) && (currentNumericValue === null || currentNumericValue === undefined || currentNumericValue === "")) {
      if (displayValue !== "") {
        setDisplayValue("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNumericValue, isFocused, isDisabled]);

  const isNegative = currentNumericValue !== null && currentNumericValue !== undefined && typeof currentNumericValue === "number" && currentNumericValue < 0;

  function buildSyntheticChangeEvent(value: string): React.ChangeEvent<HTMLInputElement> {
    return { target: { value, id: fieldName, name: fieldName } } as React.ChangeEvent<HTMLInputElement>;
  }

  function commitInputValue(rawInputValue: string) {
    let finalNumericValue: number | null = null;
    if (rawInputValue.trim() !== "") {
      finalNumericValue = parseCurrencyInput(rawInputValue, formatOptions);
    }

    if (finalNumericValue === null && restoreZeroOnEmptyBlurRef.current) {
      finalNumericValue = 0;
    }
    restoreZeroOnEmptyBlurRef.current = false;

    if (finalNumericValue !== null) {
      const finalFormatted = formatCurrency(finalNumericValue, formatOptions);
      previousValueRef.current = finalFormatted;
      setDisplayValue(finalFormatted);
      if (formStateHandlers.onChange) {
        formStateHandlers.onChange(buildSyntheticChangeEvent(finalFormatted), finalNumericValue);
      }
    } else {
      previousValueRef.current = "";
      setDisplayValue("");
      if (formStateHandlers.onChange) {
        formStateHandlers.onChange(buildSyntheticChangeEvent(""), null);
      }
    }
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    u2000SelectInputContentsOnFocus(e);
    setIsFocused(true);
    // Cellule à 0 affichée "0.00" : vider à l'édition pour permettre ".5" ; restaurer 0 si blur sans saisie
    if (currentNumericValue === 0) {
      restoreZeroOnEmptyBlurRef.current = true;
      setDisplayValue("");
      previousValueRef.current = "";
    } else {
      restoreZeroOnEmptyBlurRef.current = false;
    }
    if (onFocus) onFocus(e);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    let inputValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const configuredSeparator = decimalSeparator || ".";
    if (configuredSeparator === "." && inputValue.includes(",") && !inputValue.includes(".")) {
      inputValue = inputValue.replace(",", ".");
    } else if (configuredSeparator === "," && inputValue.includes(".") && !inputValue.includes(",")) {
      inputValue = inputValue.replace(".", ",");
    }

    const oldFormattedValue = previousValueRef.current || displayValue;
    const cleanedInput = inputValue.replace(/[^\d.,-]/g, "");
    const isPartialInput = isPartialDecimalInput(cleanedInput);
    const hasDecimalSeparator = cleanedInput.includes(".") || cleanedInput.includes(",");
    const normalizedInput = cleanedInput.replace(configuredSeparator === "," ? /\./g : /,/g, configuredSeparator);
    const decimalParts = normalizedInput.split(configuredSeparator);
    const decimalPartLength = decimalParts.length > 1 ? decimalParts[1].length : 0;
    const endsWithDecimalSeparator = cleanedInput.endsWith(".") || cleanedInput.endsWith(",");
    const isTypingDecimals = hasDecimalSeparator && (endsWithDecimalSeparator || decimalPartLength < (decimalDigits || 2));

    let parsedValue: number | null = null;
    if (!isTypingDecimals) {
      parsedValue = parseCurrencyInput(inputValue, formatOptions);
    }

    if (parsedValue === null && !isPartialInput && !isTypingDecimals && inputValue.trim() !== "" && inputValue !== "-" && inputValue !== "(") {
      setTimeout(() => {
        if (inputElementRef.current) {
          inputElementRef.current.value = oldFormattedValue;
          inputElementRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
      return;
    }

    let newFormattedValue: string;
    let valueToStore: number | null = null;

    if (inputValue.trim() === "") {
      newFormattedValue = "";
      valueToStore = null;
    } else if (inputValue === "-" || inputValue === "(") {
      newFormattedValue = inputValue;
      valueToStore = currentNumericValue;
    } else if (isTypingDecimals) {
      let cleanInputValue = inputValue;
      const formattedEnding = configuredSeparator === "," ? ",00" : ".00";
      if (hasDecimalSeparator && inputValue.includes(formattedEnding)) {
        const doubleSeparatorPattern = configuredSeparator === "," ? new RegExp(`(\\d+)[,.]?(\\d+),00$`) : new RegExp(`(\\d+)[,.]?(\\d+)\\.00$`);
        const doubleSeparatorMatch = inputValue.match(doubleSeparatorPattern);
        if (doubleSeparatorMatch) {
          cleanInputValue = inputValue.replace(new RegExp(`${formattedEnding.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`), "");
        } else if (endsWithDecimalSeparator || decimalPartLength < (decimalDigits || 2)) {
          cleanInputValue = inputValue.replace(new RegExp(`${formattedEnding.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`), "");
        }
      }
      newFormattedValue = cleanInputValue;
      const cleanedParsedValue = parseCurrencyInput(cleanInputValue, formatOptions);
      valueToStore = cleanedParsedValue !== null ? cleanedParsedValue : currentNumericValue;
    } else if (isPartialInput) {
      newFormattedValue = inputValue;
      const cleanedParsedValue = parseCurrencyInput(inputValue, formatOptions);
      valueToStore = cleanedParsedValue !== null ? cleanedParsedValue : currentNumericValue;
    } else if (parsedValue !== null) {
      newFormattedValue = formatCurrency(parsedValue, formatOptions);
      valueToStore = parsedValue;
    } else {
      newFormattedValue = oldFormattedValue;
      valueToStore = currentNumericValue;
    }

    let newCursorPosition = cursorPosition;
    if (newFormattedValue !== inputValue && newFormattedValue !== "-" && newFormattedValue !== "(") {
      newCursorPosition = getNewCursorPosition(oldFormattedValue, newFormattedValue, cursorPosition, formatOptions);
    }

    previousValueRef.current = newFormattedValue;
    setDisplayValue(newFormattedValue);

    if (formStateHandlers.onChange) {
      formStateHandlers.onChange(e, valueToStore);
    }

    setTimeout(() => {
      if (inputElementRef.current) {
        inputElementRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Tab", "Enter"];
    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
      if (e.key === "Tab" || e.key === "Enter") {
        commitInputValue(inputElementRef.current?.value ?? "");
      }
      return;
    }
    if (e.key === "-" && !allowNegative) {
      e.preventDefault();
      return;
    }
    if (e.key.length === 1) {
      const isValidChar = /[0-9.,\-()]/.test(e.key);
      if (!isValidChar) e.preventDefault();
      if (e.key === "," || e.key === ".") {
        const currentValue = inputElementRef.current?.value || "";
        const hasDecimal = currentValue.includes(".") || currentValue.includes(",");
        if (hasDecimal) {
          e.preventDefault();
          return;
        }
      }
    }
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const inputValue = e.target.value;
    if (formStateHandlers.onBlur) formStateHandlers.onBlur(e);

    // Différer après les change events en attente (ex. Tab juste après ".5")
    setTimeout(() => {
      setIsFocused(false);
      const latestValue = inputElementRef.current?.value ?? inputValue;
      commitInputValue(latestValue);
      if (onBlur) onBlur(e);
    }, 0);
  }

  const err = formStateHandlers.getErrors()[fieldName];
  const hasError =
    (formStateHandlers.getTouched()[fieldName] && err !== undefined) ||
    (formStateHandlers.isSubmitted() && err !== undefined);

  return (
    <div className="space-y-1.5">
      <U2000Label label={label} required={required} htmlFor={fieldName} />

      <div className="relative">
        {icon && <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10">{icon}</div>}

        <input
          ref={inputElementRef}
          type="text"
          id={fieldName}
          name={fieldName}
          placeholder={placeholder}
          className={`${getInputClasses(formStateHandlers, fieldName, isFocused, readonly, showNegativeInRed, isNegative, ignoreFormReadState, textAlign)} ${icon ? "pl-10" : ""}`}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={isDisabled}
          readOnly={readonly}
          required={required}
          aria-invalid={!!hasError}
          aria-describedby={err !== undefined ? `${fieldName}-error` : undefined}
        />
      </div>

      <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
};

export default U2000InputDecimal;
